import type { TimelineEntry } from './api.js';
import { groupFor, labelFor } from './timeline-labels.js';
import { useT } from './i18n.jsx';

/**
 * Vue chronologique d'un mouvement (§20).
 *
 * Chaque source est affichée SÉPARÉMENT et reste identifiable : lecture QR,
 * attelage, perte et retour du GNSS, arrêt au contrôle, saisie opérateur,
 * décision, dépose, correction.
 *
 * Ne pas fusionner est tout l'intérêt : c'est en voyant les sources côte à côte
 * qu'on comprend pourquoi une décision a été prise — et, quand elle est fausse,
 * laquelle a menti.
 */

function formatTime(iso: string): string {
  return iso.replace('T', ' ').slice(11, 23);
}

export function Timeline({ entries }: { entries: TimelineEntry[] }): JSX.Element {
  const t = useT();
  if (entries.length === 0) {
    return <p className="hint">{t('Aucune observation rattachée à ce mouvement.')}</p>;
  }

  return (
    <ol className="timeline">
      {entries.map((entry, index) => {
        const group = groupFor(entry.kind);
        return (
          <li key={`${entry.at}-${entry.kind}-${String(index)}`} className={`tl tl-${group}`}>
            <span className="tl-time">{formatTime(entry.at)}</span>
            <span className="tl-dot" aria-hidden="true" />
            <div className="tl-body">
              <div className="tl-head">
                <strong>{t(labelFor(entry.kind))}</strong>
                {entry.label !== entry.kind && <span className="tl-label">{entry.label}</span>}
              </div>
              {Object.keys(entry.details).length > 0 && (
                <dl className="tl-details">
                  {Object.entries(entry.details)
                    .filter(([, value]) => value !== null && value !== undefined)
                    .map(([key, value]) => (
                      <div key={key}>
                        <dt>{key}</dt>
                        <dd>{typeof value === 'object' ? JSON.stringify(value) : String(value)}</dd>
                      </div>
                    ))}
                </dl>
              )}
              {entry.eventId !== null && <code className="tl-event">{entry.eventId}</code>}
            </div>
          </li>
        );
      })}
    </ol>
  );
}
